import axios from "axios";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { CREATE_jwel } from "../Config/Config";

/* ════════════════════════════════════════════════════════════════
   DATA LAYER
   ----------------------------------------------------------------
   1. Endpoint : /api/ERP/GetDataFromGivenTableNamewithwhere
                 ?tableName=CREDIT_BILLMAST&where=<today's date filter>
   2. Base URL : CREATE_jwel from Config (change if retailtextiles
                 needs its own base URL).
   3. Only today's, non-cancelled bills are requested from the server.
      A client-side check repeats the same filter as a safety net.
   4. Slices come from the per-bill payment columns:
        PAIDCASH, PAIDCARD, PAIDUPI, PAIDONLINE, PAIDCHEQUE
      plus BALANCE (unpaid part of the bill) shown as "Credit".
   5. Response may be a bare array OR { data: [...] } — both handled.
   ════════════════════════════════════════════════════════════════ */
const PAY_FIELDS = [
  ["Cash", "PAIDCASH"],
  ["Card", "PAIDCARD"],
  ["UPI", "PAIDUPI"],
  ["Online", "PAIDONLINE"],
  ["Cheque", "PAIDCHEQUE"],
  ["Credit", "BALANCE"],
];

// Local date (not UTC) so "today" matches the shop's day, YYYY-MM-DD
const todayKey = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// API returns mixed key casing ("billdate", "NETAMT", "CANCELL"...)
const pick = (row, field) => {
  if (!row) return undefined;
  if (row[field] !== undefined) return row[field];
  const k = Object.keys(row).find(
    (key) => key.toLowerCase() === field.toLowerCase(),
  );
  return k ? row[k] : undefined;
};

const useBillSales = () => {
  // summary = { slices: [...], bills, qty, discount, amount }
  const [summary, setSummary] = useState({
    slices: [],
    bills: 0,
    qty: 0,
    discount: 0,
    amount: 0,
  });
  const tenantName = localStorage.getItem("tenantName");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const today = todayKey();

        // Server-side filter: only today's, non-cancelled bills come back
        const where = `CAST(BILLDATE AS DATE) = '${today}' AND CANCELL = 'NO'`;
        const url = `${CREATE_jwel}/api/ERP/GetDataFromGivenTableNamewithwhere?tableName=CREDIT_BILLMAST&where=${encodeURIComponent(where)}`;

        const res = await axios.get(url, { headers: { tenantName } });
        const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];

        // Safety net: if the backend ignores `where`, still show today only
        const todays = rows.filter(
          (r) =>
            String(pick(r, "billdate") || "").slice(0, 10) === today &&
            String(pick(r, "CANCELL") || "NO").toUpperCase() !== "YES",
        );

        const num = (r, f) => Number(pick(r, f) || 0);

        // Build one slice per payment mode
        const slices = PAY_FIELDS.map(([label, field]) => {
          const used = todays.filter((r) => num(r, field) > 0);
          return {
            category: label,
            amount: used.reduce((a, r) => a + num(r, field), 0),
            bills: used.length,
          };
        }).filter((s) => s.amount > 0);

        setSummary({
          slices,
          bills: todays.length,
          qty: todays.reduce((a, r) => a + num(r, "totqty"), 0),
          discount: todays.reduce((a, r) => a + num(r, "discount"), 0),
          // NETAMT = final bill value
          amount: todays.reduce((a, r) => a + num(r, "NETAMT"), 0),
        });
      } catch (e) {
        console.error("Bill sales fetch error", e);
      }
    };
    fetchData();
    // const id = setInterval(fetchData, 30000);
    // return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return summary;
};

/* ════════════════════════════════════════════════════════════════
   UI
   ════════════════════════════════════════════════════════════════ */

/* Fixed colour per payment mode so a mode always looks the same */
const MODE_COLORS = {
  CASH: "#00b894",
  CARD: "#6c5ce7",
  UPI: "#00cec9",
  ONLINE: "#74b9ff",
  CHEQUE: "#e0a526",
  CREDIT: "#e17055",
};
const FALLBACK_COLORS = ["#fd79a8", "#a29bfe", "#9aa5b1"];

const colorFor = (category, i) =>
  MODE_COLORS[String(category || "").toUpperCase()] ||
  FALLBACK_COLORS[i % FALLBACK_COLORS.length];

const fmtMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const fmtCompact = (n) =>
  n >= 100000
    ? `₹${(n / 100000).toFixed(1)}L`
    : n >= 1000
      ? `₹${(n / 1000).toFixed(0)}k`
      : `₹${Number(n).toFixed(0)}`;

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div
      style={{
        background: "rgba(30,20,80,0.94)",
        borderRadius: 10,
        padding: "9px 13px",
        color: "#fff",
        fontSize: 11,
        boxShadow: "0 6px 20px rgba(44,27,105,0.35)",
        minWidth: 140,
        border: "1px solid rgba(255,255,255,0.1)",
      }}
    >
      <div
        style={{
          fontWeight: 800,
          fontSize: 12,
          marginBottom: 5,
          borderBottom: "1px solid rgba(255,255,255,0.15)",
          paddingBottom: 4,
        }}
      >
        {d.category}
      </div>
      {[
        ["Bills", d.bills],
        ["Amount", fmtMoney(d.amount)],
      ].map(([k, v]) => (
        <div
          key={k}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            marginBottom: 2,
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.6)" }}>{k}</span>
          <strong>{v}</strong>
        </div>
      ))}
    </div>
  );
};

const LegendItem = ({ item, color, index, totalAmt }) => {
  const pct = totalAmt > 0 ? ((item.amount / totalAmt) * 100).toFixed(0) : 0;
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.06, duration: 0.3 }}
      whileHover={{ x: 2 }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "5px 7px",
        borderRadius: 8,
        background: `${color}14`,
        borderLeft: `3px solid ${color}`,
        cursor: "default",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: "#2d1b69",
            textTransform: "uppercase",
            letterSpacing: 0.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.category}
        </div>
        <div style={{ fontSize: 9, color: "#777", fontWeight: 600 }}>
          {item.bills} {item.bills === 1 ? "bill" : "bills"}
        </div>
      </div>

      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#2d1b69" }}>
          {fmtCompact(item.amount)}
        </div>
        <span
          style={{
            fontSize: 8,
            fontWeight: 800,
            color: "#fff",
            background: color,
            borderRadius: 10,
            padding: "1px 5px",
          }}
        >
          {pct}%
        </span>
      </div>
    </motion.div>
  );
};

const TodaysSalesPieChart = () => {
  const { slices: data, bills, qty, discount, amount } = useBillSales();

  // Pie percentages are based on the sum of the slices (payments + credit)
  const sliceTotal = data.reduce((a, d) => a + d.amount, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 20px rgba(44,27,105,0.10)",
        padding: "12px 14px 10px",
        height: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#2d1b69" }}>
            Commodity Wise Sales
          </div>
          <div style={{ fontSize: 9, color: "#aaa", marginTop: 1 }}>
            Mode-wise ·{" "}
            {new Date().toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
        </div>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
          style={{
            background: "linear-gradient(135deg,#2d1b69,#7c5cbf)",
            borderRadius: 8,
            padding: "3px 9px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: 8,
              color: "rgba(255,255,255,0.7)",
              fontWeight: 600,
            }}
          >
            TOTAL
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>
            {fmtCompact(amount)}
          </div>
        </motion.div>
      </div>

      {data.length === 0 ? (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#b8a4e8",
            gap: 6,
          }}
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ repeat: Infinity, duration: 3 }}
            style={{ fontSize: 30 }}
          >
            🪙
          </motion.div>
          <div style={{ fontSize: 11, fontWeight: 600 }}>
            No sales yet today
          </div>
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 8,
            minHeight: 0,
          }}
        >
          {/* Donut */}
          <div
            style={{
              position: "relative",
              flexShrink: 0,
              width: 150,
              height: 150,
            }}
          >
            <ResponsiveContainer width={150} height={150}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={66}
                  paddingAngle={3}
                  dataKey="amount"
                  labelLine={false}
                  animationBegin={0}
                  animationDuration={900}
                >
                  {data.map((d, i) => (
                    <Cell
                      key={i}
                      fill={colorFor(d.category, i)}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#2d1b69",
                    lineHeight: 1.1,
                  }}
                >
                  {bills}
                </div>
                <div style={{ fontSize: 9, color: "#888", fontWeight: 600 }}>
                  {bills === 1 ? "bill" : "bills"}
                </div>
              </div>
            </div>
          </div>

          {/* Legend + totals */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              overflowY: "auto",
              minWidth: 0,
              overflowX: "hidden",
              maxHeight: "100%",
            }}
          >
            {data.map((item, i) => (
              <LegendItem
                key={i}
                item={item}
                color={colorFor(item.category, i)}
                index={i}
                totalAmt={sliceTotal}
              />
            ))}

            <div
              style={{
                borderTop: "1px solid #f0eaff",
                marginTop: 2,
                paddingTop: 4,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "2px 10px",
              }}
            >
              {[
                ["Bills", bills],
                ["Total Qty", qty],
                ["Discount", fmtCompact(discount)],
                ["Net Amt", fmtCompact(amount)],
              ].map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 9, color: "#aaa", fontWeight: 600 }}>
                    {k}
                  </span>
                  <span
                    style={{ fontSize: 10, fontWeight: 800, color: "#4c3494" }}
                  >
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default TodaysSalesPieChart;
